import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Share2, Copy, Check, MessageCircle, Twitter, Loader2, Eye, EyeOff, Link2 } from "lucide-react";

const WA = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;
const TW = (text, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;

export default function ShareReport() {
  const [token, setToken] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/report/share").then((r) => { setToken(r.data.token); setEnabled(r.data.enabled); }).catch(() => {});
  }, []);

  const shareUrl = token ? `${window.location.origin}/api/share/${token}` : "";
  const shareText = "Check out my AI Career Intelligence Profile on EntranceZone AI 🚀 Discover your own career strengths:";

  const createLink = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/report/share");
      setToken(data.token); setEnabled(true); setOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create link"); }
    finally { setLoading(false); }
  };

  const handleClick = () => { if (token) setOpen((o) => !o); else createLink(); };

  const toggle = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/report/share/toggle", { enabled: !enabled });
      setEnabled(data.enabled);
      toast.success(data.enabled ? "Sharing turned on" : "Sharing turned off — link is now private");
    } catch { toast.error("Could not update"); }
    finally { setLoading(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Link copied!"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="relative inline-block">
      <button data-testid="share-report-btn" onClick={handleClick} disabled={loading}
        className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-8 py-3.5 text-base font-bold text-emerald-300 transition-all hover:bg-emerald-400/15 disabled:opacity-60">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Share2 className="h-5 w-5" />} Share my report
      </button>

      {open && token && (
        <div data-testid="share-report-panel" className="absolute left-1/2 bottom-full z-30 mb-3 w-80 -translate-x-1/2 rounded-2xl border border-white/12 bg-[#131C2E] p-5 text-left shadow-2xl">
          <div className="flex items-center justify-between">
            <p className="font-display text-sm font-semibold text-white flex items-center gap-2"><Link2 className="h-4 w-4 text-cyan-400" /> Your shareable link</p>
            <button data-testid="share-toggle-btn" onClick={toggle} title={enabled ? "Turn off sharing" : "Turn on sharing"}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${enabled ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-slate-400"}`}>
              {enabled ? <><Eye className="h-3 w-3" /> Public</> : <><EyeOff className="h-3 w-3" /> Private</>}
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
            <input data-testid="share-link-input" readOnly value={shareUrl} className="flex-1 bg-transparent text-xs text-slate-300 outline-none" />
            <button data-testid="share-copy-btn" onClick={copy} className="text-slate-400 hover:text-cyan-400">
              {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <a data-testid="share-whatsapp-btn" href={WA(`${shareText} ${shareUrl}`)} target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366]/15 px-3 py-2.5 text-sm font-semibold text-[#4ae08a] hover:bg-[#25D366]/25">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a data-testid="share-twitter-btn" href={TW(shareText, shareUrl)} target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/8 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:bg-white/12">
              <Twitter className="h-4 w-4" /> X / Twitter
            </a>
          </div>
          <p className="mt-3 text-[10px] text-slate-500">Anyone with this link can view your profile summary while sharing is Public.</p>
        </div>
      )}
    </div>
  );
}
